/**
 * エピソードのオフラインダウンロードフック
 *
 * 署名付きURL取得 → downloadWithResume → IndexedDB保存 の流れを管理。
 * 複数エピソードのキュー処理にも対応。
 */
import { useCallback, useRef } from 'react'
import { toast } from 'sonner'

import { getEpisodeHighlights, getEpisodeProgress, getSignedUrl, getTranscription } from '@/api/api'
import { resolveDirectUrl } from '@/api/client'
import {
  deleteOfflineEpisode,
  discardPendingDownload,
  isEpisodeOffline,
  type OfflineEpisode,
  type OfflineMusicMeta,
  saveAudio,
  saveOfflineEpisode,
} from '@/lib/db'
import { DownloadHttpError, downloadWithResume } from '@/lib/download-utils'
import { fetchThumbnailBlob } from '@/lib/thumbnail-utils'
import { useDownloadStore } from '@/store/useDownloadStore'

/**
 * 中間チャンクを残して再開に備えるべきエラーかを判定する
 *
 * ネットワーク断（TypeError）と一時的なサーバエラー（408/429/5xx）だけを再開対象とし、
 * それ以外（401/403/404、容量不足、HTML レスポンス等）は再試行しても回復しないため即座に破棄する。
 */
const isResumableError = (error: unknown): boolean => {
  if (error instanceof DownloadHttpError) return error.isRetryable
  // fetch のネットワークエラーは TypeError として投げられる
  return error instanceof TypeError
}

/** 中間データの破棄に失敗しても後続の状態更新を止めない */
const discardQuietly = async (episodeId: number) => {
  try {
    await discardPendingDownload(episodeId)
  } catch (error) {
    console.warn('[Download] 中間データの破棄に失敗しました:', error)
  }
}

/**
 * 進行中ダウンロードの AbortController
 *
 * フックのインスタンスをまたいで中断できるようモジュールレベルで保持する
 * （一覧・ドロワー・ストレージ管理など複数箇所から同じエピソードを操作するため）
 */
const activeAbortControllers = new Map<number, AbortController>()

/**
 * ダウンロードを直列実行するための Promise チェーン
 *
 * 複数エピソードを同時に取得すると帯域・メモリ・サーバ接続数を争って失敗しやすいため、
 * 同時実行は常に 1 件に限定する。
 */
let downloadChain: Promise<void> = Promise.resolve()

/** 直列キューにタスクを積む（前のタスクが失敗しても後続は実行される） */
const enqueueSerial = (task: () => Promise<void>): Promise<void> => {
  const result = downloadChain.then(task, task)
  downloadChain = result.catch(() => undefined)
  return result
}

// ---------------------------------------------------------------------------
// 単一エピソードダウンロード
// ---------------------------------------------------------------------------

export const useEpisodeDownload = () => {
  const { enqueue, startDownload, updateProgress, setStatus, completeDownload, cancelDownload } = useDownloadStore()

  /**
   * エピソードをダウンロードしてIndexedDBに保存
   */
  const downloadEpisode = useCallback(
    async (params: {
      episodeId: number
      productId: number
      domain: string
      title: string
      mimeType: string
      /** ダウンロードする画質（省略時は原本） */
      quality?: string
      /** 音楽ドメインでのみ渡す付随情報 */
      music?: OfflineMusicMeta
    }) => {
      const { episodeId, productId, domain, title, mimeType, quality = 'original', music } = params

      // 既にオフライン保存済みか
      const alreadyOffline = await isEpisodeOffline(episodeId)
      if (alreadyOffline) {
        // 保存済みなのに中断レコードが残っている場合は孤児データなので掃除する
        await discardQuietly(episodeId)
        toast.info('既にダウンロード済みです')
        return
      }

      // 既に待機中／実行中なら二重に積まない
      if (activeAbortControllers.has(episodeId)) {
        toast.info('既にダウンロード待ちです')
        return
      }

      // キューに追加
      enqueue({ episodeId, productId, domain, title })

      const controller = new AbortController()
      activeAbortControllers.set(episodeId, controller)

      // 画質指定付きのストリームパス（original はクエリなし）
      const streamPath =
        quality === 'original'
          ? `/e/${episodeId}/stream`
          : `/e/${episodeId}/stream?quality=${encodeURIComponent(quality)}`

      // 同時ダウンロードは失敗しやすいため、順番が回ってくるまで待つ
      return enqueueSerial(async () => {
        // 待機中に一時停止された場合は開始せずに破棄する
        if (controller.signal.aborted) {
          activeAbortControllers.delete(episodeId)
          cancelDownload(episodeId)
          return
        }

        try {
          startDownload(episodeId)

          // 1. ダウンロード（中断時は中間チャンクを残し、次回 Range で続きから再開）
          // 署名付きURLは期限切れを避けるためリクエスト直前に取得する
          const blob = await downloadWithResume({
            domain,
            episodeId,
            mimeType,
            onProgress: (prog) => updateProgress(episodeId, Math.round(prog.percentage)),
            productId,
            quality,
            resolveUrl: async () => resolveDirectUrl(await getSignedUrl(streamPath)),
            signal: controller.signal,
            title,
          })

          // 2. IndexedDB保存
          setStatus(episodeId, 'saving')

          // サムネイルを取得（失敗しても本体ダウンロードは成功させる）
          const thumbnailBlob = await fetchThumbnailBlob(productId)

          const episode: OfflineEpisode = {
            blob,
            domain,
            downloadedAt: Date.now(),
            episodeId,
            fileSize: blob.size,
            mimeType,
            music,
            productId,
            quality,
            thumbnailBlob: thumbnailBlob ?? undefined,
            title,
          }
          await saveOfflineEpisode(episode)

          // 保存が完了してから中間チャンクを破棄する
          //（blob は中間チャンクを参照しているため、先に消すと読めなくなるブラウザがある）
          await discardQuietly(episodeId)

          // 機内モードでも続きから開けるよう、現在の進捗とハイライトを端末に保存する（取得時に保存される）
          await Promise.all([getEpisodeProgress(episodeId), getEpisodeHighlights(episodeId)]).catch((err: unknown) => {
            console.warn('[Download] 進捗・ハイライトの取得に失敗しました:', err)
          })

          // 2.5. Englishドメインは文字起こしデータも事前取得してaudiosストアに保存
          if (domain === 'english') {
            try {
              const transcription = await getTranscription(String(episodeId))
              await saveAudio(domain, String(productId), String(episodeId), title, blob, transcription)
              console.log('[Download] English: transcription pre-fetched and saved to audios store')
            } catch (err) {
              console.warn('[Download] English: failed to pre-fetch transcription (audio still saved):', err)
            }
          }

          // 3. 完了
          completeDownload(episodeId)
          toast.success(`「${title}」をダウンロードしました`)
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            // ユーザーによる一時停止。中間チャンクは残し、次回クリックで続きから再開する
            cancelDownload(episodeId)
            toast.info(`「${title}」のダウンロードを一時停止しました`)
            return
          }
          // 回復見込みのないエラーは中間チャンクを残さずに破棄する
          if (!isResumableError(error)) {
            await discardQuietly(episodeId)
          }
          const msg = error instanceof Error ? error.message : 'ダウンロードに失敗しました'
          setStatus(episodeId, 'error', msg)
          toast.error(`ダウンロード失敗: ${msg}`)
        } finally {
          activeAbortControllers.delete(episodeId)
        }
      })
    },
    [enqueue, startDownload, updateProgress, setStatus, completeDownload, cancelDownload],
  )

  /**
   * 進行中ダウンロードを一時停止する
   *
   * 中間チャンクは保持されるため、同じエピソードを再度ダウンロードすると続きから再開する。
   * キュー待機中（まだ通信が始まっていない）のものは即座にキューから外す。
   * @param episodeId 省略時は進行中の全ダウンロードを停止する
   */
  const abort = useCallback(
    (episodeId?: number) => {
      const abortOne = (id: number) => {
        activeAbortControllers.get(id)?.abort()
        activeAbortControllers.delete(id)
        // 待機中は fetch が始まっておらず AbortError が飛ばないため、ここで状態を戻す
        if (useDownloadStore.getState().downloads.get(id)?.status === 'queued') {
          cancelDownload(id)
        }
      }

      if (episodeId === undefined) {
        for (const id of [...activeAbortControllers.keys()]) {
          abortOne(id)
        }
        return
      }

      abortOne(episodeId)
    },
    [cancelDownload],
  )

  /**
   * オフラインエピソードを削除
   *
   * IndexedDB だけ消してもストアに status: 'done' が残っているとボタンが保存済み表示のままになるため、
   * ストアのエントリも併せて破棄する。
   */
  const removeOfflineEpisode = useCallback(
    async (episodeId: number) => {
      await deleteOfflineEpisode(episodeId)
      cancelDownload(episodeId)
      toast.success('ダウンロードデータを削除しました')
    },
    [cancelDownload],
  )

  return { downloadEpisode, abort, removeOfflineEpisode }
}

// ---------------------------------------------------------------------------
// プロダクト一括ダウンロード
// ---------------------------------------------------------------------------

export const useProductDownload = () => {
  const { downloadEpisode, abort } = useEpisodeDownload()
  const { clearQueue } = useDownloadStore()
  const abortedRef = useRef(false)

  /**
   * プロダクト内の全エピソードを順次ダウンロード
   */
  const downloadProduct = useCallback(
    async (
      episodes: Array<{
        episodeId: number
        productId: number
        domain: string
        title: string
        mimeType: string
        music?: OfflineMusicMeta
      }>,
    ) => {
      abortedRef.current = false

      for (const ep of episodes) {
        if (abortedRef.current) break

        const already = await isEpisodeOffline(ep.episodeId)
        if (already) continue

        await downloadEpisode(ep)
      }
    },
    [downloadEpisode],
  )

  const abortAll = useCallback(() => {
    abortedRef.current = true
    abort()
    clearQueue()
  }, [abort, clearQueue])

  return { downloadProduct, abortAll }
}
